import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// @ts-ignore - unstable_HistoryRouter may not be in types but is available in React Router v6.4+
import { BrowserRouter, Navigate, NavLink, Route, Routes } from 'react-router';
import { _keys, BadImplementationException, composeQueryParams, composeUrl, exists, Module, removeItemFromArray } from '@nu-art/ts-common';
import { ThunderDispatcher } from '../../core/thunder-dispatcher.js';
import { mouseEventHandler, stopPropagation } from '../../utils/tools.js';
import { AwaitModules } from '../../components/AwaitModules/AwaitModules.js';
import { AwaitSync } from '../../components/AwaitSync/AwaitSync.js';
export const dispatch_onLocationChanged = new ThunderDispatcher('__onLocationChanged');
class ModuleFE_RoutingV2_Class extends Module {
    // ######################## Inner Data ########################
    routesMapByKey = {};
    routesMapByPath = {};
    constructor() {
        super();
        // Listen to browser navigation events (back/forward buttons, etc.)
        window.addEventListener('popstate', () => {
            dispatch_onLocationChanged.dispatchUI(window.location.pathname);
        });
    }
    // ######################## Public Functions ########################
    goToRoute(route, params, hash) {
        const fullPath = this.getFullPath(route.key);
        try {
            const queryString = composeQueryParams(params);
            const search = queryString.length > 0 ? `?${queryString}` : '';
            const url = composeUrl(fullPath, params, hash);
            if (url === window.location.href)
                return this.logWarning(`attempting to set same route: ${fullPath}${search}`);
            // Also update window.location to trigger BrowserRouter's popstate listener
            // This ensures React Router detects the navigation change
            window.history.pushState({}, '', url);
            // Manually dispatch popstate event to trigger BrowserRouter update
            window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
        }
        catch (e) {
            this.logError(`cannot resolve route for route: `, route, e);
            throw e;
        }
    }
    redirect(route, params) {
        const url = composeUrl(this.getFullPath(route.key), params);
        return _jsx(Navigate, { to: url });
    }
    generateRoutes(rootRoute) {
        // This needs to be a component in order to be build the routes on rendering after modules are awaited
        this.buildRouteMap(rootRoute);
        const RoutesRenderer = () => _jsx(Routes, { children: this.routeBuilder(rootRoute) });
        // Use BrowserRouter - unstable_HistoryRouter has compatibility issues
        // We'll use window.location changes to trigger React Router updates
        return _jsx(BrowserRouter, { children: _jsx(RoutesRenderer, {}) });
    }
    buildRouteMap(route, _path = '') {
        const path = `${_path}/`;
        this.routesMapByKey[route.key] = { route, fullPath: _path };
        this.routesMapByPath[_path] = route;
        route.children?.map(route => this.buildRouteMap(route, `${path}${route.path}`));
    }
    routeBuilder = (route) => {
        this.logDebug(`building route for: ${route.key}`);
        const path = this.routesMapByKey[route.key].fullPath;
        const routes = route.children || [];
        const indicesRoutes = routes?.filter(route => route.index);
        if (indicesRoutes && indicesRoutes.length > 1)
            throw new BadImplementationException(`more than one index route found in ${path}: ${indicesRoutes.map(r => r.key).join(', ')}`);
        const indexRoute = indicesRoutes?.[0];
        let _indexRoute;
        if (indexRoute) {
            const Component = this.resolveRouteComponent(indexRoute);
            if (indexRoute.path) {
                this.logDebug(`index route redirect to path: ${path}/${indexRoute.path}`);
                // force redirect to a different route
                _indexRoute = _jsx(Route, { index: true, element: _jsx(Navigate, { to: `${path}/${indexRoute.path}` }) });
            }
            else {
                // default index route renderer
                this.logDebug(`index route render component: ${path}/${indexRoute.path}`);
                _indexRoute = _jsx(Route, { index: true, Component: Component, element: indexRoute.element });
                removeItemFromArray(routes, indexRoute);
            }
        }
        if (route.fallback)
            this.logDebug(`fallback: ${path}`);
        const children = route.children?.filter(route => {
            return route.enabled?.() ?? true;
        }) ?? [];
        const Component = this.resolveRouteComponent(route);
        return _jsxs(Route, { path: route.path, Component: Component, element: route.element, children: [_indexRoute, children.map(route => this.routeBuilder(route)), route.fallback && _jsx(Route, { path: "*", element: _jsx(Navigate, { to: path }) })] }, route.key);
    };
    resolveRouteComponent = (route) => {
        if (!route.Component)
            return undefined;
        const shouldAwaitModules = !!route.modulesToAwait;
        const shouldAwaitSync = !!route.awaitSync;
        //No awaiting of any type
        if (!shouldAwaitModules && !shouldAwaitSync)
            return route.Component;
        //Awaiting both modules and sync
        if (shouldAwaitModules && shouldAwaitSync) {
            if (route.Component.prototype?.render) {
                const Component = route.Component;
                return () => _jsx(AwaitSync, { customLoader: route.awaitSyncLoader, children: _jsx(AwaitModules, { modules: route.modulesToAwait, customLoader: route.awaitModulesLoader, children: _jsx(Component, {}) }) });
            }
            const component = route.Component;
            return () => _jsx(AwaitSync, { customLoader: route.awaitSyncLoader, children: _jsx(AwaitModules, { modules: route.modulesToAwait, customLoader: route.awaitModulesLoader, children: component({}) }) });
        }
        //Awaiting only modules
        if (shouldAwaitModules && !shouldAwaitSync) {
            //route.Component is a class component
            if (route.Component.prototype?.render) {
                const Component = route.Component;
                return () => _jsx(AwaitModules, { modules: route.modulesToAwait, customLoader: route.awaitModulesLoader, children: _jsx(Component, {}) });
            }
            //route.Component is a function component
            const component = route.Component;
            return () => _jsx(AwaitModules, { modules: route.modulesToAwait, customLoader: route.awaitModulesLoader, children: component({}) });
        }
        //Awaiting only sync
        if (shouldAwaitModules && !shouldAwaitSync) {
            if (route.Component.prototype.render) {
                const Component = route.Component;
                return () => _jsx(AwaitSync, { customLoader: route.awaitSyncLoader, children: _jsx(Component, {}) });
            }
            //route.Component is a function component
            const component = route.Component;
            return () => _jsx(AwaitSync, { customLoader: route.awaitSyncLoader, children: component({}) });
        }
    };
    getRouteByKey(routeKey) {
        return this.routesMapByKey[routeKey]?.route;
    }
    getFullPath(routeKey) {
        const route = this.routesMapByKey[routeKey];
        if (!route)
            throw new BadImplementationException(`Cannot find full path for route key: ${routeKey}`);
        return route.fullPath;
    }
    getCurrentRouteKey() {
        return this.routesMapByPath[window.location.pathname];
    }
    // ######################## Query Param Methods ########################
    /**
     * Get all query parameters from the current URL (decoded)
     */
    getQueryParams() {
        const params = this.getEncodedQueryParams();
        _keys(params).forEach(key => {
            const _param = params[key];
            if (!exists(_param))
                return;
            const value = `${params[key]}`;
            params[key] = decodeURIComponent(value);
        });
        return params;
    }
    /**
     * Get a single query parameter from the current URL
     * @param key - The query parameter key
     * @returns The decoded value, null if key exists but value is empty, or undefined if key doesn't exist
     */
    getQueryParameter(key) {
        const queryParams = this.getQueryParams();
        const value = queryParams[key];
        if (value === undefined && Object.keys(queryParams).includes(key))
            return null;
        return value;
    }
    /**
     * Replace all query parameters on the current route
     * @param queryParams - The query parameters to set
     */
    setQuery(queryParams) {
        const encodedQueryParams = this.encodeUrlParams(queryParams);
        this.updateQueryParams(encodedQueryParams);
    }
    /**
     * Add or update a single query parameter on the current route
     * @param key - The query parameter key
     * @param value - The query parameter value
     */
    addQueryParam(key, value) {
        const decodedQueryParams = this.getQueryParams();
        decodedQueryParams[key] = value;
        this.updateQueryParams(decodedQueryParams);
    }
    /**
     * Remove a single query parameter from the current route
     * @param key - The query parameter key to remove
     */
    removeQueryParam(key) {
        const encodedQueryParams = this.getEncodedQueryParams();
        delete encodedQueryParams[key];
        this.updateQueryParams(encodedQueryParams);
    }
    // ######################## URL Utility Methods ########################
    /**
     * Get the current location object (compatible with BrowserHistory.getCurrent())
     * @returns Object with pathname and search properties
     */
    getCurrent() {
        return {
            pathname: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash,
        };
    }
    /**
     * Get the current URL pathname
     * @returns The current pathname
     */
    getCurrentUrl() {
        return window.location.pathname;
    }
    /**
     * Get the window origin
     * @returns The origin (protocol + hostname + port)
     */
    getOrigin() {
        return window.location.origin;
    }
    // ######################## Navigation Methods ########################
    /**
     * Convert location descriptor to URL string using composeUrl
     */
    locationToUrl(location) {
        // Parse search params if provided as string
        const params = {};
        if (location.search) {
            const searchParams = new URLSearchParams(location.search.startsWith('?') ? location.search.substring(1) : location.search);
            searchParams.forEach((value, key) => {
                params[key] = value;
            });
        }
        const hash = location.hash?.startsWith('#') ? location.hash.substring(1) : location.hash;
        return composeUrl(location.pathname, params, hash);
    }
    /**
     * Navigate to a pathname with optional query params (adds to history)
     * @param location - Location descriptor with pathname and optional search
     */
    push(location) {
        const url = this.locationToUrl(location);
        window.history.pushState({}, '', url);
        window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
    }
    /**
     * Replace current history entry with new pathname and optional query params
     * @param location - Location descriptor with pathname and optional search
     */
    replace(location) {
        const url = this.locationToUrl(location);
        window.history.replaceState({}, '', url);
        window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
    }
    // ######################## Private Helper Methods ########################
    getEncodedQueryParams() {
        const queryParams = {};
        let queryAsString = window.location.search;
        if (!queryAsString || queryAsString.length === 0)
            return {};
        while (queryAsString.startsWith('?') || queryAsString.startsWith('/?')) {
            if (queryAsString.startsWith('?'))
                queryAsString = queryAsString.substring(1);
            else if (queryAsString.startsWith('/?'))
                queryAsString = queryAsString.substring(2);
            else
                break;
        }
        const query = queryAsString.split('&');
        return query.map(param => {
            const parts = param.split('=');
            return { key: parts[0], value: parts[1]?.length === 0 ? undefined : parts[1] };
        }).reduce((toRet, param) => {
            toRet[param.key] = param.value;
            return toRet;
        }, queryParams);
    }
    composeQuery(queryParams) {
        const queryAsString = composeQueryParams(queryParams);
        if (queryAsString.length === 0)
            return '';
        return queryAsString;
    }
    encodeUrlParams(queryParams) {
        const encodedQueryParams = { ...queryParams };
        _keys(encodedQueryParams).forEach(key => {
            const value = encodedQueryParams[key];
            if (!value) {
                delete encodedQueryParams[key];
                return;
            }
            encodedQueryParams[key] = encodeURIComponent(value);
        });
        return encodedQueryParams;
    }
    updateQueryParams(encodedQueryParams) {
        const search = this.composeQuery(encodedQueryParams);
        const url = `${window.location.pathname}${search ? `?${search}` : ''}`;
        window.history.replaceState({}, '', url);
        // Manually dispatch popstate event to trigger BrowserRouter update
        window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
    }
}
export const TS_NavLink = (props) => {
    const { route, children, ignoreClickOnSameRoute, ..._props } = props;
    const fullPath = ModuleFE_RoutingV2.getFullPath(route.key);
    if (!fullPath)
        throw new BadImplementationException(`Route with key ${route.key} is not defined in routing module`);
    return _createElement(NavLink, { ..._props, to: fullPath, key: route.key, onClick: e => {
            const pathname = window.location.pathname;
            if (props.ignoreClickOnSameRoute && pathname === fullPath)
                stopPropagation(e);
        }, onMouseUp: e => mouseEventHandler(e, {
            middle: () => window.open(fullPath, '_blank'),
        }) }, children);
};
export const ModuleFE_RoutingV2 = new ModuleFE_RoutingV2_Class();
// ######################## Utility Functions ########################
/**
 * Encode URL query parameters
 * @param queryParams - Query parameters to encode
 * @returns Encoded query parameters
 */
export function encodeUrlParams(queryParams) {
    const encodedQueryParams = { ...queryParams };
    _keys(encodedQueryParams).forEach(key => {
        const value = encodedQueryParams[key];
        if (!value) {
            delete encodedQueryParams[key];
            return;
        }
        encodedQueryParams[key] = encodeURIComponent(value);
    });
    return encodedQueryParams;
}
/**
 * Compose a query string from query parameters
 * @param queryParams - Query parameters to compose
 * @returns Query string (without leading ?)
 */
export function composeQuery(queryParams) {
    const queryAsString = composeQueryParams(queryParams);
    if (queryAsString.length === 0)
        return '';
    return queryAsString;
}
/**
 * Compose a full URL with query parameters
 * @param url - Base URL
 * @param queryParams - Optional query parameters
 * @returns Full URL with query string
 */
export function composeURL(url, queryParams) {
    const queryAsString = composeQuery(queryParams);
    return `${url}${queryAsString.length > 0 ? `?${queryAsString}` : ''}`;
}
//# sourceMappingURL=ModuleFE_RoutingV2.js.map